import { Component, input, output, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ComponentSize } from '../types';

export interface MegaMenuColumn {
  title: string;
  items: MegaMenuItem[];
}

export interface MegaMenuItem {
  label: string;
  description?: string;
  icon?: string;
  route?: string;
  action?: () => void;
  disabled?: boolean;
  badge?: string;
}

@Component({
  selector: 'app-mega-menu',
  imports: [CommonModule, RouterModule],
  template: `
    <div class="relative" #container>
      <ng-content select="[trigger]"></ng-content>
      
      @if (isOpen()) {
        <div
          #menu
          [class]="menuClasses"
          role="menu"
          (click)="$event.stopPropagation()">
          <div [class]="gridClasses">
            @for (column of columns(); track column.title) {
              <div>
                <h3 [class]="columnTitleClasses">{{ column.title }}</h3>
                <ul class="space-y-1 mt-2">
                  @for (item of column.items; track item.label) {
                    <li>
                      @if (item.route) {
                        <a
                          [routerLink]="item.route"
                          [class]="getItemClasses(item)"
                          (click)="closeMenu()">
                          <div class="flex items-start gap-3">
                            @if (item.icon) {
                              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                              </svg>
                            }
                            <div class="flex-1">
                              <div class="flex items-center gap-2">
                                <span class="font-medium">{{ item.label }}</span>
                                @if (item.badge) {
                                  <span class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    {{ item.badge }}
                                  </span>
                                }
                              </div>
                              @if (item.description) {
                                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ item.description }}</p>
                              }
                            </div>
                          </div>
                        </a>
                      } @else {
                        <button
                          type="button"
                          [disabled]="item.disabled"
                          [class]="getItemClasses(item)"
                          (click)="handleAction(item)">
                          <div class="flex items-start gap-3">
                            @if (item.icon) {
                              <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                              </svg>
                            }
                            <div class="flex-1">
                              <div class="flex items-center gap-2">
                                <span class="font-medium">{{ item.label }}</span>
                                @if (item.badge) {
                                  <span class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    {{ item.badge }}
                                  </span>
                                }
                              </div>
                              @if (item.description) {
                                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ item.description }}</p>
                              }
                            </div>
                          </div>
                        </button>
                      }
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: []
})
export class MegaMenuComponent {
  columns = input<MegaMenuColumn[]>([]);
  columnsCount = input<number>(3);
  size = input<ComponentSize>('md');

  itemClickEvent = output<MegaMenuItem>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChild('menu', { static: false }) menu!: ElementRef<HTMLElement>;

  isOpen = signal(false);

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.container?.nativeElement?.contains(target)) return;
    this.closeMenu();
  }

  handleAction(item: MegaMenuItem): void {
    if (item.disabled) return;
    if (item.action) {
      item.action();
    }
    this.itemClickEvent.emit(item);
    this.closeMenu();
  }

  closeMenu(): void {
    this.isOpen.set(false);
  }

  getItemClasses(item: MegaMenuItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };

    const classes = [
      'w-full',
      'text-left',
      'rounded-md',
      'transition-colors',
      'duration-150',
      sizeMap[this.size()]
    ];

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    classes.push('text-gray-700', 'dark:text-gray-300');

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 w-full max-w-4xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-6 mt-1';
  }

  get gridClasses(): string {
    return `grid gap-6 grid-cols-${this.columnsCount()}`;
  }

  get columnTitleClasses(): string {
    return 'text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider';
  }
}

